import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MarkdownRenderer } from "@/components/chat/markdown-renderer";
import { useTenant } from "@/context/tenant-context";
import { onboardingApi } from "@/api/onboarding";
import { queryKeys } from "@/lib/query-keys";
import { TenantOnboardingCard } from "@/pages/tenant/settings/TenantSettingsOverview";
import { Globe, Monitor, Sparkles, Users } from "lucide-react";

/**
 * The settings popup's Company profile page: what the onboarding wizard
 * saved, read back from `tenant_onboarding`. Read only. To change an answer,
 * re-run the wizard from the card at the top.
 */
export function TenantCompanyProfile() {
  const { tenantId } = useTenant();
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.onboarding.byCompany(tenantId),
    queryFn: () => onboardingApi.getStatus(tenantId),
    enabled: !!tenantId,
  });

  const departments = data?.departments ?? [];
  const itSystems = data?.itSystems ?? [];

  return (
    <div className="space-y-5">
      <TenantOnboardingCard />

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading...</p>
      ) : isError ? (
        <p className="text-sm text-danger">Couldn't load the company profile.</p>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Globe size={18} />
                About your organisation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ProfileField label="Website URL">
                {data?.websiteUrl && /^https?:\/\//i.test(data.websiteUrl) ? (
                  <a
                    href={data.websiteUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-accent hover:underline"
                  >
                    {data.websiteUrl}
                  </a>
                ) : data?.websiteUrl ? (
                  // Only real web addresses become a link; anything else is
                  // shown as plain text so it can never run as a script.
                  <p className="text-sm text-text-primary">{data.websiteUrl}</p>
                ) : (
                  <NotProvided />
                )}
              </ProfileField>
              <ProfileField label="What does your company do?">
                {data?.businessDescription ? (
                  <p className="whitespace-pre-wrap text-sm text-text-primary">
                    {data.businessDescription}
                  </p>
                ) : (
                  <NotProvided />
                )}
              </ProfileField>
              <ProfileField label="Brand Guidelines">
                {data?.brandGuidelinesContent ? (
                  <p className="text-sm text-text-primary">
                    Loaded ({data.brandGuidelinesContent.length.toLocaleString()} characters)
                  </p>
                ) : (
                  <NotProvided />
                )}
              </ProfileField>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users size={18} />
                Organisation Structure
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TagList items={departments} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Monitor size={18} />
                IT Systems
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TagList items={itSystems} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles size={18} />
                Company profile written by AI
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.enrichedProfile ? (
                <div className="rounded-xl border border-border bg-background p-5">
                  <MarkdownRenderer content={data.enrichedProfile} />
                </div>
              ) : (
                <p className="text-sm text-text-muted">
                  Not written yet. The wizard writes it at the end.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function ProfileField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-text-muted">{label}</p>
      {children}
    </div>
  );
}

function TagList({ items }: { items: string[] }) {
  if (items.length === 0) return <NotProvided />;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <Badge key={item} variant="secondary">
          {item}
        </Badge>
      ))}
    </div>
  );
}

function NotProvided() {
  return <p className="text-sm text-text-muted">Not provided</p>;
}
